import { Component, Element, Prop, h } from '@stencil/core';

import { actions as pageParamsActions } from '../actions/actions.page-params';
import { onQuestionsInit } from '../actions/actions.questions';
import { onVehicleConditionInit, onVehicleConditionUnloaded, onVehicleConditionUpdate, validateForm } from '../actions/actions.vehicle-conditions';
import { state as answerState, on as onAnswerStateChange } from '../store/store.answers';
import { state as exteriorState, on as onExteriorStateChange } from '../store/store.exterior';
import { state as interiorState, on as onInteriorStateChange } from '../store/store.interior';
import { state as mechanicalState, on as onMechanicalStateChange } from '../store/store.mechanical';
import { state as detailState, on as onDetailStateChange } from '../store/store.question-vehicle';
import { on as onVehicleConditionsStateChange, state as vehicleConditionsState } from '../store/store.vehicle-conditions';
import { trackConditionTabSelection } from '../tracking/tracking.conditions';

@Component({
  tag: 'conditions-state-hydrator',
  shadow: true,
})
export class ConditionStateHydrator {
  @Element() el: HTMLElement;

  @Prop() colorId: number = 0;
  @Prop() drivetrainId: number = 0;
  @Prop() drivetrain: string = '';
  @Prop() engineId: number = 0;
  @Prop() makeId: number = 0;
  @Prop() mileage: number = 0;
  @Prop() modelId: number = 0;
  @Prop() transmissionId: number = 0;
  @Prop() trimId: number = 0;
  @Prop() vin: string = '';
  @Prop() yearId: number = 0;

  // Analytics
  @Prop() entry: string;
  @Prop() offerCode: string;
  @Prop() lnxCode: string;
  @Prop() category: string;
  @Prop() theme: string;

  @Prop() formSubmitted: boolean;
  @Prop() leadFormUrl: string;
  @Prop() showConditionTierAlert: boolean;
  @Prop() allowIneligibleVehicleSubmission: boolean = false;

  async componentWillLoad() {
    onVehicleConditionInit({ formSubmitted: this.formSubmitted, leadFormUrl: this.leadFormUrl, showConditionTierAlert: this.showConditionTierAlert });

    // boostrap props
    pageParamsActions.onReceivedParams({ entry: this.entry, offerCode: this.offerCode, lnxCode: this.lnxCode, category: this.category });

    const vehicle = {
      yearId: this.yearId,
      makeId: this.makeId,
      modelId: this.modelId,
      trimId: this.trimId,
      colorId: this.colorId,
      transmissionId: this.transmissionId,
      engineId: this.engineId,
      drivetrainId: this.drivetrainId,
      drivetrain: this.drivetrain,
      vin: this.vin,
      mileage: this.mileage,
    };

    // await so we don't render until we have the questions loaded. The question API will set some
    // vehicle information that impacts the rendering await so we don't render the wrong thing initially
    await onQuestionsInit(vehicle, this.allowIneligibleVehicleSubmission);
    trackConditionTabSelection('exterior');

    validateForm();

    this.setupStateProps();

    onAnswerStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onExteriorStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onInteriorStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onDetailStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onMechanicalStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    onVehicleConditionsStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });
  }

  componentWillRender() {
    onVehicleConditionUpdate({ formSubmitted: this.formSubmitted, leadFormUrl: this.leadFormUrl, showConditionTierAlert: this.showConditionTierAlert });
  }

  disconnectedCallback() {
    onVehicleConditionUnloaded();
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    this.el.childNodes.forEach((element) => {
      element[prop] = value;
    });
  }

  // explicitly declare initial state props
  setupStateProps() {
    this.el.childNodes.forEach((element) => {
      // Answers
      element['conditions'] = answerState.conditions;

      // Interior
      element['questionsCarpet'] = interiorState.questionsCarpet;
      element['questionsDashboard'] = interiorState.questionsDashboard;
      element['questionsDashboardLights'] = interiorState.questionsDashboardLights;
      element['questionsFrontSeats'] = interiorState.questionsFrontSeats;
      element['questionsHeadliner'] = interiorState.questionsHeadliner;
      element['questionsMoldingTrim'] = interiorState.questionsMoldingTrim;
      element['questionsOdors'] = interiorState.questionsOdors;
      element['questionsRearSeats'] = interiorState.questionsRearSeats;
      element['questionsThirdRowSeats'] = interiorState.questionsThirdRowSeats;

      element['questionsCargoBed'] = interiorState.questionsCargoBed;

      // Exterior
      element['questionsBrakes'] = exteriorState.questionsBrakes;
      element['questionsFrontBumper'] = exteriorState.questionsFrontBumper;
      element['questionsDriverFrontFender'] = exteriorState.questionsDriverFrontFender;
      element['questionsDriverFrontDoor'] = exteriorState.questionsDriverFrontDoor;
      element['questionsPassengerFrontFender'] = exteriorState.questionsPassengerFrontFender;
      element['questionsPassengerFrontDoor'] = exteriorState.questionsPassengerFrontDoor;
      element['questionsPassengerRearDoor'] = exteriorState.questionsPassengerRearDoor;
      element['questionsPassengerRearPanel'] = exteriorState.questionsPassengerRearPanel;
      element['questionsGrille'] = exteriorState.questionsGrille;
      element['questionsHeadlights'] = exteriorState.questionsHeadlights;
      element['questionsHood'] = exteriorState.questionsHood;
      element['questionsTrunk'] = exteriorState.questionsTrunk;
      element['questionsMirrors'] = exteriorState.questionsMirrors;
      element['questionsFrontMoldingTrim'] = exteriorState.questionsFrontMoldingTrim;
      element['questionsRearMoldingTrim'] = exteriorState.questionsRearMoldingTrim;
      element['questionsDriverMoldingTrim'] = exteriorState.questionsDriverMoldingTrim;
      element['questionsPassengerMoldingTrim'] = exteriorState.questionsPassengerMoldingTrim;
      element['questionsDriverRockerPanel'] = exteriorState.questionsDriverRockerPanel;
      element['questionsPassengerRockerPanel'] = exteriorState.questionsPassengerRockerPanel;
      element['questionsRearBumper'] = exteriorState.questionsRearBumper;
      element['questionsDriverRearDoor'] = exteriorState.questionsDriverRearDoor;
      element['questionsDriverRearPanel'] = exteriorState.questionsDriverRearPanel;
      element['questionsRearWindow'] = exteriorState.questionsRearWindow;
      element['questionsRoof'] = exteriorState.questionsRoof;
      element['questionsSideWindows'] = exteriorState.questionsSideWindows;
      element['questionsTiresRims'] = exteriorState.questionsTiresRims;
      element['questionsTaillights'] = exteriorState.questionsTaillights;
      element['questionsWindshield'] = exteriorState.questionsWindshield;

      // Vehicle Details
      element['vehicleType'] = detailState.vehicleType;
      element['isThirdRowOptional'] = detailState.isThirdRowOptional;
      element['numberOfRows'] = detailState.numberOfRows;
      element['numberOfDoors'] = detailState.numberOfDoors;

      // Mechanical
      element['questionsEngine'] = mechanicalState.questionsEngine;
      element['questionsTransmission'] = mechanicalState.questionsTransmission;
      element['questionsSteering'] = mechanicalState.questionsSteering;
      element['questionsExhaust'] = mechanicalState.questionsExhaust;
      element['questionsSuspension'] = mechanicalState.questionsSuspension;
      element['questionsHeatingAir'] = mechanicalState.questionsHeatingAir;
      element['questionsBatteryElectrical'] = mechanicalState.questionsBatteryElectrical;
      element['questionsStereo'] = mechanicalState.questionsStereo;
      element['questionsOdometer'] = mechanicalState.questionsOdometer;
      element['questionsSunMoonRoof'] = mechanicalState.questionsSunMoonRoof;
      element['questionsAirbags'] = mechanicalState.questionsAirbags;
      element['questionsFrameStructural'] = mechanicalState.questionsFrameStructural;

      element['conditionPageFrontBadge'] = exteriorState.conditionPageFrontBadge;
      element['conditionPageDriverBadge'] = exteriorState.conditionPageDriverBadge;
      element['conditionPagePassengerBadge'] = exteriorState.conditionPagePassengerBadge;
      element['conditionPageRearBadge'] = exteriorState.conditionPageRearBadge;
      element['conditionPageWindowsLightsBadge'] = exteriorState.conditionPageWindowsLightsBadge;

      element['conditionPageSche'] = interiorState.conditionPageSche;
      element['conditionPageDbl'] = interiorState.conditionPageDbl;

      // Condition Tier Selection
      element['conditionTierSelection'] = vehicleConditionsState.conditionTierSelection;
      element['isDescriptionOpen'] = vehicleConditionsState.isDescriptionOpen;
      element['showConditionTierAlert'] = vehicleConditionsState.showConditionTierAlert;
      element['showModal'] = vehicleConditionsState.showModal;

      // theme
      element['theme'] = this.theme;
    });
  }

  render() {
    return (
      <div class="hydration-state hydration-state--conditions">
        <slot />
      </div>
    );
  }
}
